import { useState } from 'react';
import {
  ChevronDown, Shield, Target, Brain, Dumbbell,
  FileText, BookOpen, Globe, Landmark, Atom,
  Newspaper, BarChart3, MapPin, CheckCircle, AlertTriangle,
  PenTool, Users,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const CAPF_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "CAPF (AC) Syllabus 2026 – Paper I & Paper II Complete Topics",
  "description": "Complete CAPF AC 2026 syllabus. Paper I: General Ability & Intelligence (250 marks, Objective). Paper II: General Studies, Essay & Comprehension (200 marks, Descriptive). PET standards, SSB Interview (150 marks), selection procedure.",
  "author": { "@type": "Organization", "name": "EduPulseAI" },
  "publisher": { "@type": "Organization", "name": "EduPulseAI", "url": "https://edupulseai.co.in" },
  "datePublished": "2026-08-20",
  "dateModified": "2026-08-20",
  "mainEntityOfPage": { "@type": "WebPage", "@id": "https://edupulseai.co.in/syllabus/capf" },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://edupulseai.co.in" },
      { "@type": "ListItem", "position": 2, "name": "CAPF AC Syllabus 2026" }
    ]
  }
};

/* ── Paper I topics ────────────────────────────────────────────────────── */
const PAPER1_TOPICS = [
  {
    id: 'mental', title: 'General Mental Ability', color: '#6366F1', Icon: Brain,
    points: [
      'Logical reasoning and quantitative aptitude including numerical ability.',
      'Data interpretation — tables, graphs, charts.',
      'Questions designed to test logical reasoning and analytical capability.',
    ],
  },
  {
    id: 'science', title: 'General Science', color: '#10B981', Icon: Atom,
    points: [
      'General awareness and scientific temper.',
      'Comprehension and appreciation of scientific phenomena of everyday observation.',
      'New areas of importance: Information Technology, Biotechnology, Environmental Science.',
    ],
  },
  {
    id: 'current', title: 'Current Events of National & International Importance', color: '#F59E0B', Icon: Newspaper,
    points: [
      'Awareness of current events of national and international importance.',
      'Broad areas: culture, music, arts, literature, sports, governance.',
      'Societal and developmental issues, industry, business, globalisation.',
      'Interplay among nations.',
    ],
  },
  {
    id: 'polity', title: 'Indian Polity and Economy', color: '#EC4899', Icon: Landmark,
    points: [
      'Political system and the Constitution of India.',
      'Social systems and public administration.',
      'Economic development in India.',
      'Regional and international security issues.',
      'Human rights including its indicators.',
    ],
  },
  {
    id: 'history', title: 'History of India', color: '#F97316', Icon: BookOpen,
    points: [
      'Social, economic and political aspects of Indian History.',
      'Areas of growth of nationalism.',
      'Freedom movement.',
    ],
  },
  {
    id: 'geography', title: 'Indian and World Geography', color: '#3B82F6', Icon: MapPin,
    points: [
      'Physical, social and economic aspects of geography pertaining to India and the World.',
    ],
  },
];

/* ── PET standards ─────────────────────────────────────────────────────── */
const PET = [
  { event: '100 Meter Race', men: 'In 16 Seconds',         women: 'In 18 Seconds' },
  { event: '800 Meter Race', men: 'In 03 Min. 45 Sec.',    women: 'In 4 Min. 45 Sec.' },
  { event: 'Long Jump',      men: '3.5 Meters (3 Chances)',women: '3.0 Meters (3 Chances)' },
  { event: 'Shot Put (7.26 Kg)', men: '4.5 Meters (3 Chances)', women: '—' },
];

/* ── Selection stages ──────────────────────────────────────────────────── */
const STAGES = [
  { no: 1, label: 'Written Examination', sub: 'Paper I (250) + Paper II (200) = 450 marks', color: '#6366F1' },
  { no: 2, label: 'Physical Standards Test (PST)', sub: 'Height, weight, chest — prescribed standards', color: '#10B981' },
  { no: 3, label: 'Physical Efficiency Test (PET)', sub: '100m, 800m, Long Jump, Shot Put', color: '#F59E0B' },
  { no: 4, label: 'Interview / Personality Test', sub: '150 marks — only PET-qualified candidates', color: '#EC4899' },
  { no: 5, label: 'Medical Standards Test (MST)', sub: 'Conducted after Interview/PT', color: '#8B5CF6' },
  { no: 6, label: 'Final Merit List', sub: 'Written Exam + Interview marks combined', color: '#3B82F6' },
];

const TABS = [
  { id: 'pattern', label: 'Exam Pattern', Icon: Target },
  { id: 'paper1',  label: 'Paper I – GAI', Icon: Brain },
  { id: 'paper2',  label: 'Paper II – GSE', Icon: PenTool },
];

function AccordionItem({ title, points, color, Icon, isOpen, onToggle }) {
  return (
    <div className="rounded-2xl border bg-white overflow-hidden transition-shadow hover:shadow-md"
      style={{ borderColor: isOpen ? color + '40' : '#F1F5F9' }}>
      <button onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 cursor-pointer border-none bg-transparent text-left">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: color + '18' }}>
          <Icon size={14} style={{ color }} />
        </div>
        <span className="flex-1 font-bold text-sm text-slate-800">{title}</span>
        <ChevronDown size={15} className="text-slate-300 shrink-0 transition-transform"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 border-t" style={{ borderColor: color + '20' }}>
          <div className="mt-3 flex flex-col gap-2">
            {points.map((p, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: color }} />
                <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════════════════════════════ */
export default function CAPFSyllabusPage() {
  const [tab, setTab]   = useState('pattern');
  const [openP1, setOpenP1] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="CAPF AC Syllabus 2026 – Paper I & Paper II Complete Topics | UPSC CAPF"
        description="Complete CAPF AC 2026 syllabus from UPSC official notification. Paper I: General Ability & Intelligence (250 marks, Objective, bilingual). Paper II: Essay & Comprehension (200 marks, Descriptive). PET standards table, Interview/PT (150 marks). Written date: 19 July 2026."
        keywords="CAPF syllabus 2026, CAPF AC syllabus, CAPF paper 1 syllabus, CAPF paper 2 syllabus, CAPF exam pattern 2026, UPSC CAPF 2026, CAPF AC selection procedure, CAPF PET standards, CAPF interview marks"
        url="/syllabus/capf"
        jsonLd={CAPF_JSON_LD}
      />

      {/* ── Hero ── */}
      <div className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#0F172A 0%,#1a1a2e 55%,#16213e 100%)' }}>
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle,#F9A8D4,transparent)' }} />
        <div className="absolute bottom-0 left-10 w-40 h-40 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle,#818CF8,transparent)' }} />

        <div className="w-full px-6 pt-8 pb-6 relative">
          <div className="flex items-center gap-2 mb-4">
            <Shield size={13} className="text-pink-400" />
            <span className="text-xs text-pink-300 font-medium">Defence Exams</span>
            <span className="text-white/20 text-xs">›</span>
            <span className="text-xs text-white/50">CAPF (AC) Syllabus 2026</span>
          </div>
          <h1 className="font-extrabold text-2xl text-white leading-tight mb-1">CAPF (AC) Syllabus 2026</h1>
          <p className="text-sm text-pink-200 mb-5">Central Armed Police Forces (Assistant Commandants) · UPSC Official Syllabus</p>

          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Paper I',        value: '250',     color: '#A5B4FC' },
              { label: 'Paper II',       value: '200',     color: '#F9A8D4' },
              { label: 'Interview/PT',   value: '150',     color: '#6EE7B7' },
              { label: 'Written Date',   value: '19 Jul 2026', color: '#FCD34D' },
              { label: 'Paper I Type',   value: 'Objective', color: '#93C5FD' },
              { label: 'Paper II Type',  value: 'Descriptive', color: '#FCA5A5' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.08)' }}>
                <span className="text-xs font-extrabold" style={{ color: s.color }}>{s.value}</span>
                <span className="text-[10px] text-white/40">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full px-6 pb-0">
          <div className="flex gap-1 border-t border-white/10 pt-1">
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setTab(id)}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold cursor-pointer border-none transition-all relative"
                style={{ background: 'transparent', color: tab === id ? '#fff' : 'rgba(255,255,255,0.38)' }}>
                <Icon size={13} />
                {label}
                {tab === id && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 rounded-full"
                    style={{ background: 'linear-gradient(90deg,#EC4899,#8B5CF6)' }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="w-full px-6 py-6 pb-16">

        {/* ══ EXAM PATTERN ══ */}
        {tab === 'pattern' && (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="font-bold text-base text-slate-800">Selection Procedure</h2>
              <p className="text-xs text-slate-400 mt-0.5">Written → PST/PET → Interview → Medical → Merit</p>
            </div>

            {/* selection flow */}
            <div className="flex flex-col gap-3">
              {STAGES.map(s => (
                <div key={s.no} className="flex items-center gap-4 bg-white border border-slate-100 rounded-2xl px-5 py-4 shadow-sm">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-extrabold shrink-0"
                    style={{ background: s.color + '18', color: s.color }}>{s.no}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-slate-800">{s.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{s.sub}</p>
                  </div>
                  <CheckCircle size={16} style={{ color: s.color }} className="shrink-0" />
                </div>
              ))}
            </div>

            {/* papers */}
            <h3 className="font-bold text-sm text-slate-700 mt-1">Written Examination — Papers</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderTop: '4px solid #6366F1' }}>
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-500">Paper I · Objective</p>
                      <h3 className="font-extrabold text-sm text-slate-800 mt-0.5">General Ability & Intelligence</h3>
                      <p className="text-xs text-slate-400 mt-0.5">10:00 AM – 12:00 Noon</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-2xl font-extrabold text-indigo-600">250</p>
                      <p className="text-[10px] text-slate-300">marks</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 mt-3">
                    {['Multiple choice objective type questions', 'Set in English as well as Hindi (bilingual)', 'Covers: Mental Ability, Science, Current Events, Polity, History, Geography'].map((p, i) => (
                      <div key={i} className="flex gap-2">
                        <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 bg-indigo-300" />
                        <p className="text-xs text-slate-500">{p}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderTop: '4px solid #EC4899' }}>
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-pink-500">Paper II · Descriptive</p>
                      <h3 className="font-extrabold text-sm text-slate-800 mt-0.5">General Studies, Essay & Comprehension</h3>
                      <p className="text-xs text-slate-400 mt-0.5">2:00 PM – 5:00 PM</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-2xl font-extrabold text-pink-600">200</p>
                      <p className="text-[10px] text-slate-300">marks</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 mt-3">
                    {['Part A — Essay: 80 marks (Hindi or English)', 'Part B — Comprehension & Précis: 120 marks (English only)', 'Paper I must qualify before Paper II is evaluated'].map((p, i) => (
                      <div key={i} className="flex gap-2">
                        <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 bg-pink-300" />
                        <p className="text-xs text-slate-500">{p}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* PET table */}
            <div>
              <h3 className="font-bold text-sm text-slate-700 mb-3">Physical Efficiency Test (PET) Standards</h3>
              <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
                <div className="grid grid-cols-3 text-xs font-extrabold uppercase tracking-widest bg-slate-50 border-b border-slate-100">
                  <div className="px-4 py-3 text-slate-500">Event</div>
                  <div className="px-4 py-3 text-blue-600">Men</div>
                  <div className="px-4 py-3 text-pink-600">Women</div>
                </div>
                <div className="divide-y divide-slate-50">
                  {PET.map((row, i) => (
                    <div key={i} className="grid grid-cols-3 text-xs">
                      <div className="px-4 py-3 font-semibold text-slate-700">{row.event}</div>
                      <div className="px-4 py-3 text-slate-600">{row.men}</div>
                      <div className="px-4 py-3 text-slate-600">{row.women}</div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-2 px-1">* Long jump: candidate must clear and land beyond the prescribed distance. Both starting and finishing lines are inclusive as disqualification markers.</p>
            </div>

            {/* tie breaking */}
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-5">
              <h3 className="font-bold text-sm text-slate-800 mb-3">Tie Breaking Principle</h3>
              <div className="flex flex-col gap-2">
                {[
                  'Candidate securing more marks in written total will be ranked higher.',
                  'If still equal — more marks in Paper I (General Ability & Intelligence) ranks higher.',
                  'If still equal — candidate senior in age will be ranked higher.',
                  'If tie persists — resolved at the discretion of the Commission.',
                ].map((rule, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold shrink-0"
                      style={{ background: '#F5F3FF', color: '#8B5CF6' }}>{i + 1}</div>
                    <p className="text-sm text-slate-600 leading-relaxed">{rule}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Interview */}
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users size={14} className="text-emerald-600" />
                <p className="text-xs font-bold text-emerald-700">Interview / Personality Test — 150 Marks</p>
              </div>
              <p className="text-xs text-emerald-700 leading-relaxed">
                Only candidates declared qualified in PST/PET are called for the Interview/Personality Test conducted by UPSC.
                Candidates declared unqualified in the Physical Efficiency Test will <strong>not</strong> be called.
                Final merit = Written Examination marks + Interview/Personality Test marks.
              </p>
            </div>
          </div>
        )}

        {/* ══ PAPER I ══ */}
        {tab === 'paper1' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-base text-slate-800">Paper I — General Ability & Intelligence</h2>
                <p className="text-xs text-slate-400 mt-0.5">250 Marks · Objective (MCQ) · Bilingual (Hindi & English) · 6 Areas</p>
              </div>
              <button onClick={() => setOpenP1(openP1 === 'all' ? null : 'all')}
                className="text-xs font-bold text-indigo-600 px-3 py-1.5 rounded-xl bg-indigo-50 border-none cursor-pointer shrink-0">
                {openP1 === 'all' ? 'Collapse all' : 'Expand all'}
              </button>
            </div>

            {PAPER1_TOPICS.map(topic => (
              <AccordionItem
                key={topic.id}
                title={topic.title}
                points={topic.points}
                color={topic.color}
                Icon={topic.Icon}
                isOpen={openP1 === topic.id || openP1 === 'all'}
                onToggle={() => setOpenP1(prev => prev === topic.id ? null : topic.id)}
              />
            ))}

            <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4 mt-1">
              <p className="text-xs font-bold text-indigo-700 mb-1">📝 Paper I Note</p>
              <p className="text-xs text-indigo-700 leading-relaxed">
                Questions are of Objective (Multiple Answers) type. Paper I is evaluated first — candidates must obtain minimum qualifying marks to have Paper II evaluated.
              </p>
            </div>
          </div>
        )}

        {/* ══ PAPER II ══ */}
        {tab === 'paper2' && (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="font-bold text-base text-slate-800">Paper II — General Studies, Essay & Comprehension</h2>
              <p className="text-xs text-slate-400 mt-0.5">200 Marks · Descriptive · 2:00 PM – 5:00 PM</p>
            </div>

            {/* Part A */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderTop: '4px solid #F97316' }}>
              <div className="px-5 py-4 border-b border-slate-50" style={{ background: '#FFF7ED' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-orange-100 flex items-center justify-center">
                      <FileText size={14} className="text-orange-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-orange-600">Part A</p>
                      <h3 className="font-extrabold text-sm text-slate-800">Essay</h3>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-extrabold text-orange-600">80</p>
                    <p className="text-[10px] text-slate-400">marks</p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 flex flex-col gap-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-orange-50 text-orange-700 w-fit mb-1">
                  Medium: Hindi or English (candidate's choice)
                </div>
                {[
                  'Essay questions to be answered in long narrative form.',
                  'Modern Indian history — especially the freedom struggle.',
                  'Geography of India and the World.',
                  'Indian Polity and Economy.',
                  'Knowledge of security and human rights issues.',
                  'Analytical ability.',
                ].map((p, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 bg-orange-400" />
                    <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Part B */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderTop: '4px solid #EC4899' }}>
              <div className="px-5 py-4 border-b border-slate-50" style={{ background: '#FDF2F8' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-pink-100 flex items-center justify-center">
                      <PenTool size={14} className="text-pink-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-pink-600">Part B</p>
                      <h3 className="font-extrabold text-sm text-slate-800">Comprehension, Précis & Language Skills</h3>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-extrabold text-pink-600">120</p>
                    <p className="text-[10px] text-slate-400">marks</p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 flex flex-col gap-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-pink-50 text-pink-700 w-fit mb-1">
                  Medium: English only
                </div>
                {[
                  'Comprehension passages.',
                  'Précis writing.',
                  'Developing counter arguments.',
                  'Simple grammar.',
                  'Other communications / language skills testing.',
                ].map((p, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 bg-pink-400" />
                    <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* marks summary */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Essay (Part A)', marks: 80, color: '#F97316', note: 'Hindi / English' },
                { label: 'Comprehension (Part B)', marks: 120, color: '#EC4899', note: 'English only' },
                { label: 'Paper II Total', marks: 200, color: '#8B5CF6', note: 'Descriptive' },
              ].map(s => (
                <div key={s.label} className="rounded-2xl border bg-white p-4 shadow-sm text-center"
                  style={{ borderTop: `3px solid ${s.color}` }}>
                  <p className="text-2xl font-extrabold" style={{ color: s.color }}>{s.marks}</p>
                  <p className="text-[10px] font-bold text-slate-500 mt-0.5">{s.label}</p>
                  <p className="text-[10px] text-slate-300 mt-0.5">{s.note}</p>
                </div>
              ))}
            </div>

            {/* important note */}
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={13} className="text-amber-600" />
                <p className="text-xs font-bold text-amber-700">Important Language Rule</p>
              </div>
              <p className="text-xs text-amber-700 leading-relaxed">
                In Paper II, candidates <strong>must write answers only in the medium allowed</strong> by the Commission for each component. The medium for the Essay Component must be indicated on the Attendance List and the Answer Book. <strong>No credit will be given</strong> for the Essay if the candidate indicates a different medium on the Attendance List and Answer Book.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
